import type { PImage, PContainer } from '../engine/aliases'
import { phaserUtil } from '../engine/phaserUtil-sidecar'
import { pubSub } from '../engine/pubSub-sidecar'
import type { IEntity } from '../entities/entitiesUtils'
import { entitiesUtils } from '../entities/entitiesUtils-sidecar'
import { l } from '../lib/imports'
import { common } from '../main/common-sidecar'
import { echoFonts } from '../proto/echo/echoFonts-sidecar'
import type { ISPD } from '../sprites/sprites'
import { sprites } from '../sprites/sprites-sidecar'
import { spritesUtil } from '../sprites/spritesUtil-sidecar'
import { clickEater } from './clickEater-sidecar'
import type { IFontData2 } from './fontData2'
import type { IStevieText2 } from './stevieTexts2'
import { stevieTexts2 } from './stevieTexts2-sidecar'

export interface IButton2 extends IEntity {
  // image: PImage
  x: number
  y: number
  text: IStevieText2
  textOx: number
  textOy: number
  imageBackground: PImage
  imageAdditional: PImage
  imageIcon: PImage | null
  isOver: boolean
  isMouseDown: boolean
  isMouseRightDown: boolean

  isSelected: boolean

  iconOx: number
  iconOy: number

  frames: ISPD[]
  frames2: ISPD[]
  scale: number
  onClick: () => void
  onMouseOver: (() => void) | null
  onMouseOut: (() => void) | null

  tag: string
}

const objs: IButton2[] = []

let w = 50
let h = 10
// export const defaultFramesEcho: ISPD[] = sprites.echoButton
export const defaultFramesBasic: ISPD[] = sprites.voyTileChecker1 // sprites.underButtonBasic
export const defaultFramesLite: ISPD[] = sprites.voyTileChecker1 // sprites.underButtonLite
export const defaultFramesMowerSm: ISPD[] = sprites.voyTileChecker1 //sprites.buttonMowerSm

let defaultFont = echoFonts.getData().font4
export const setDefaultFont = (font: IFontData2) => {
  defaultFont = font
}
let defaultFrames = defaultFramesBasic
export const setDefaultFrames = (frames: ISPD[]) => {
  defaultFrames = frames
}

export function create(
  layer: PContainer,
  x: number,
  y: number,
  text: string,
  onClick: () => void,
  frames: ISPD[] = defaultFrames,
) {
  let imageBackground = spritesUtil.createImage(
    layer,
    frames[0] || defaultFrames[0],
  )

  let imageAdditional = spritesUtil.createImage(layer, defaultFrames[0])

  imageAdditional.visible = false

  let imageIcon = null

  let _text = stevieTexts2.create(defaultFont, 0, 0, layer)
  stevieTexts2.setText(_text, text)
  stevieTexts2.setScale(_text, 4)

  let obj: IButton2 = {
    eType: 'button',
    x: 0,
    y: 0,
    textOx: 0,
    textOy: 0,
    id: common.createId(),
    idLoaded: 0,
    imageBackground,
    imageAdditional,
    imageIcon,
    isMouseDown: false,
    isMouseRightDown: false,
    isOver: false,
    text: _text,
    frames,
    frames2: [],
    onClick,
    onMouseOver: null,
    onMouseOut: null,
    isDead: false,
    isSelected: false,
    scale: 1,
    iconOx: -68,
    iconOy: 4,
    tag: '',
  }
  moveTo(obj, x, y)
  objs.push(obj)

  phaserUtil.setInteractive(imageBackground)

  imageBackground.on('pointerover', () => {
    obj.isOver = true
    if (obj.onMouseOver) {
      obj.onMouseOver()
    }
  })
  imageBackground.on('pointerout', () => {
    obj.isOver = false
    obj.isMouseDown = false
    if (obj.onMouseOut) {
      obj.onMouseOut()
    }
  })

  imageBackground.on('pointerdown', () => {
    clickEater.tempEat()

    let scene = common.getPhaserScene()
    if (scene.game.input.activePointer.rightButtonDown()) {
      obj.isMouseRightDown = true
    } else {
      obj.isMouseDown = true
    }
  })
  imageBackground.on('pointerup', () => {
    clickEater.tempEat()

    if (obj.isMouseDown) {
      obj.isMouseDown = false
      if (obj.imageBackground.visible) {
        pubSub.emit('gui:click-button')
        obj.onClick()
      }
    }
    if (obj.isMouseRightDown) {
      obj.isMouseRightDown = false
    }
  })
  return obj
}

// export function setImages(
//   obj: IButton2,
//   normal: ISPD,
//   highlighted: ISPD,
//   selected: ISPD,
// ) {
//   obj.frames = [normal, highlighted, selected]
// }

export function setLocked(obj: IButton2) {
  // setIcon(obj, sprites.underUiLock[0])
}

export function setTag(obj: IButton2, tag: string) {
  obj.tag = tag
}

export function setIcon(
  obj: IButton2,
  sprite: ISPD,
  scale: number = 4,
  ox: number = -68,
  oy: number = 4,
) {
  if (!obj.imageIcon) {
    obj.imageIcon = spritesUtil.createImage(
      obj.imageBackground.parentContainer,
      sprite,
    )
  }

  spritesUtil.setPackedFrame(obj.imageIcon, sprite)
  obj.imageIcon.scale = scale
  obj.iconOx = ox
  obj.iconOy = oy
  moveTo(obj, obj.x, obj.y)
}

export function setAdditionalHighlightAndSelectedImages(
  obj: IButton2,
  highlighted: ISPD,
  selected: ISPD,
) {
  obj.frames2 = [highlighted, selected]
}

export function setScale(obj: IButton2, scale: number) {
  obj.scale = scale
  obj.imageBackground.scale = scale * 4
  stevieTexts2.setScale(obj.text, scale)
  moveTo(obj, obj.imageBackground.x, obj.imageBackground.y)
}

export function setSelected(obj: IButton2, isSelected: boolean) {
  obj.isSelected = isSelected
}

export function setText(obj: IButton2, text: string) {
  stevieTexts2.setText(obj.text, text)
}

export function setTextTint(obj: IButton2, tint: number) {
  stevieTexts2.setTint(obj.text, tint)
}

export function setVisible(obj: IButton2, visible: boolean) {
  obj.imageBackground.visible = visible
  stevieTexts2.setVisible(obj.text, visible)
}

export function setOnClick(obj: IButton2, onClick: () => void) {
  obj.onClick = onClick
}
export function setOnMouseOver(obj: IButton2, onMouseOver: () => void) {
  obj.onMouseOver = onMouseOver
}
export function setOnMouseOut(obj: IButton2, onMouseOut: () => void) {
  obj.onMouseOut = onMouseOut
}
export function kill(obj: IButton2) {
  obj.isDead = true
}
export function killBulk(objs: IButton2[]) {
  l.forEach(objs, (c) => {
    kill(c)
  })
  return []
}

export function updateAll() {
  l.forEach(objs, (c) => {
    update(c)
  })
  entitiesUtils.removeDead(objs, (c) => {
    c.imageBackground.destroy()
    stevieTexts2.kill(c.text)
    if (c.imageIcon) {
      c.imageIcon.destroy()
    }
  })
}

export function update(obj: IButton2) {
  let frameIdx = 0

  if (obj.isMouseDown) {
    frameIdx = 2
  } else if (obj.isOver) {
    frameIdx = 1
  } else {
    frameIdx = 0
  }

  if (obj.isSelected) {
    frameIdx += 3
  }

  spritesUtil.setPackedFrame(
    obj.imageBackground,
    obj.frames[frameIdx % obj.frames.length] ||
      defaultFrames[frameIdx % defaultFrames.length],
  )

  if (obj.frames2 && obj.frames2.length > 0) {
    if (obj.isMouseDown || obj.isSelected) {
      frameIdx = 1
    } else if (obj.isOver) {
      frameIdx = 0
    } else {
      frameIdx = -1
    }
    if (frameIdx !== -1) {
      spritesUtil.setPackedFrame(obj.imageAdditional, obj.frames2[frameIdx])
      obj.imageAdditional.visible = true
    } else {
      obj.imageAdditional.visible = false
    }
  }
}

export function moveTo(obj: IButton2, x: number, y: number) {
  obj.x = x
  obj.y = y
  obj.imageBackground.x = x
  obj.imageBackground.y = y
  obj.imageAdditional.x = x
  obj.imageAdditional.y = y
  if (obj.imageIcon) {
    obj.imageIcon.x = x + obj.iconOx
    obj.imageIcon.y = y + obj.iconOy
  }
  stevieTexts2.moveTo(
    obj.text,
    x - obj.imageBackground.width / 2 + obj.textOx,
    y + obj.textOy,
  )
}

export function setTextOffset(obj: IButton2, x: number, y: number) {
  obj.textOx = x
  obj.textOy = y
  moveTo(obj, obj.x, obj.y)
}
export function setTextScale(obj: IButton2, s: number) {
  stevieTexts2.setScale(obj.text, s)
}

export function setBulkVisible(objs: IButton2[], isVisible: boolean) {
  l.forEach(objs, (c) => setVisible(c, isVisible))
}

export const highlight = (obj: IButton2) => {
  obj.isSelected = true
}
export const unhighlight = (obj: IButton2) => {
  obj.isSelected = false
}

export const executeClick = (obj: IButton2) => {
  obj.onClick()
}
